import { get } from './http';
import { PaginatedResponse } from './types';

// Types sourced from https://github.com/ev-proddev/community
// Paths below are relative to that repo root (community-dto/src/main/java/com/experticity/community/dto/).

// ── Enums ─────────────────────────────────────────────────────────────────────

/** @sync-ignore — community repo has TextFormat.java (HTML, TEXT only); MARKDOWN value may be frontend-only or from a different class */
export enum GuidelinesFormat {
  HTML = 'HTML',
  MARKDOWN = 'MARKDOWN',
  TEXT = 'TEXT',
}

// ── Types ─────────────────────────────────────────────────────────────────────

/** @source CommunityPageDto.java */
export type CommunityImage = {
  uri?: string;
  originalUri?: string;
  source?: string;
  width?: number;
  height?: number;
  isDefaultImage?: boolean;
};

/** @source CommunityPageDto.java */
export type Guidelines = {
  id?: number;
  pageId?: number;
  body: string;
  format?: GuidelinesFormat;
  version?: number;
  published?: boolean;
  creationDate?: string;
  lastModified?: string;
  lastPublished?: string;
};

/** @source CommunityPageDto.java */
export type PageSection = {
  id?: number;
  pageId?: number;
  name: string;
  body?: string;
  format?: GuidelinesFormat;
  sortOrder?: number;
  creationDate?: string;
  lastModified?: string;
};

/** @source CommunityPageDto.java */
export type PageCategory = {
  id?: number;
  name?: string;
  iconClass?: string;
};

/** @source CommunityPageDto.java */
export type CommunityRole = {
  pageId?: number;
  userId: number;
  roleType: string;
  overrideEmail?: string;
  primaryEmail?: string;
  creationDate?: string;
};

/** @source CommunityPageDto.java */
export type CommunityPage = {
  id?: number;
  pageKey?: string;
  pageKeys?: string[];
  name: string;
  description?: string;
  published?: boolean;
  discoverable?: boolean;
  avatar?: string;
  images?: Record<string, CommunityImage>;
  teamKey?: string;
  teamId?: number;
  teamClassId?: number;
  orgId?: number;
  membershipStatus?: string;
  membershipType?: string;
  teaserBrandIds?: number[];
  teaserTeamIds?: number[];
  inviteMessage?: string;
  marketingFooterUrl?: string;
  isPrivatePro?: boolean;
  guidelines?: Guidelines;
  sections?: PageSection[];
  communityRoles?: CommunityRole[];
  categories?: PageCategory[];
};

/** @source CommunityMemberDto.java */
export type CommunityMember = {
  uuid?: string;
  displayName?: string;
  avatar?: string;
  hero?: string;
  communityMemberSince?: string;
  location?: string;
  profileLink?: string;
  followed?: boolean;
  privateProfile?: boolean;
  membershipStatus?: string;
};

/** @source TeamClassPageKeyDto.java */
export type TeamClassDto = {
  teamClassId: number;
  name?: string;
  description?: string;
};

/** @source TeamClassPageKeyDto.java */
export type TeamClassPageKey = {
  teamClass: TeamClassDto;
  pageKey: string;
  pageUrl: string;
  imageUrls?: Record<string, string>;
};

// ── API ───────────────────────────────────────────────────────────────────────

export function getCommunityPageByKey(pageKey: string): Promise<CommunityPage> {
  return get<CommunityPage>(`/xapi/community/pub/1.0/page/PROGRAM/${pageKey}`);
}

export function getCommunityPageByTeamKey(teamKey: string): Promise<CommunityPage> {
  return get<CommunityPage>(`/xapi/community/pub/1.0/page/PROGRAM/team/${teamKey}`);
}

export function getCommunityMembers(
  pageId: number,
  options: { size: number; sort?: string },
): Promise<PaginatedResponse<CommunityMember>> {
  return get<PaginatedResponse<CommunityMember>>(`/xapi/community/ext/1.0/page/id/${pageId}/members`, {
    params: { size: options.size, sort: options.sort },
  });
}

export function getTeamClassPageKeys(): Promise<TeamClassPageKey[]> {
  return get<TeamClassPageKey[]>('/xapi/community/ext/1.0/teamClass/pageKey');
}
